import { CalculatedMetrics, GlobalAnalysisFilters, PriceBucket, MarketCapBucket, GapPercentBucket, VolumeBucket, HeatmapCell, CorrelationData, BestSetup, BoxPlotData, BucketDefinition, BucketAnalysisData } from '../types';
import { calculateMedian } from './calculations';

// Funzione helper per calcolare medie sicure
const safeAverage = (values: number[]): number => {
  const validValues = values.filter(v => isFinite(v) && !isNaN(v));
  if (validValues.length === 0) return 0;
  return validValues.reduce((sum, val) => sum + val, 0) / validValues.length;
};

// Funzione helper per calcolare la varianza
const calculateVariance = (values: number[]): number => {
  const validValues = values.filter(v => isFinite(v) && !isNaN(v));
  if (validValues.length === 0) return 0;
  const mean = safeAverage(validValues);
  const squaredDiffs = validValues.map(value => Math.pow(value - mean, 2));
  return safeAverage(squaredDiffs);
};

// Funzione helper per calcolare la deviazione standard
const calculateStdDev = (values: number[]): number => {
  return Math.sqrt(calculateVariance(values));
};

// Funzioni helper per i calcoli dei bucket
export const calculateGapPercentage = (row: CalculatedMetrics): number => {
  // Usa il valore Gap % già calcolato nel dataset
  const gapPercent = Math.abs((row['Gap %'] || 0) * 100);
  return gapPercent;
};

export const isRedClose = (row: CalculatedMetrics): boolean => {
  return row.Close < row.Open;
};

// Definizioni dei bucket aggiornate - RIMOSSO il minimo di 30%
export const gapBuckets: BucketDefinition[] = [
  { label: "0% - 10%", min: 0, max: 10 },
  { label: "10% - 20%", min: 10, max: 20 },
  { label: "20% - 30%", min: 20, max: 30 },
  { label: "30% - 40%", min: 30, max: 40 },
  { label: "40% - 50%", min: 40, max: 50 },
  { label: "50% - 60%", min: 50, max: 60 },
  { label: "60% - 70%", min: 60, max: 70 },
  { label: "70% - 80%", min: 70, max: 80 },
  { label: "80% - 100%", min: 80, max: 100 },
  { label: "100% - 120%", min: 100, max: 120 },
  { label: "120% - 150%", min: 120, max: 150 },
  { label: "150% - 200%", min: 150, max: 200 },
  { label: "200% - 300%", min: 200, max: 300 },
  { label: "300% - 500%", min: 300, max: 500 },
  { label: "500% <", min: 500, max: Infinity }
];

// CORRETTO: Bucket per fasce di prezzo con etichette numeriche
export const priceBuckets: BucketDefinition[] = [
  { label: "$0 - $0.5", min: 0, max: 0.5 },
  { label: "$0.5 - $1", min: 0.5, max: 1 },
  { label: "$1 - $1.5", min: 1, max: 1.5 },
  { label: "$1.5 - $2", min: 1.5, max: 2 },
  { label: "$2 - $3", min: 2, max: 3 },
  { label: "$3 - $4", min: 3, max: 4 },
  { label: "$4 - $5", min: 4, max: 5 },
  { label: "$5 - $7.5", min: 5, max: 7.5 },
  { label: "$7.5 - $10", min: 7.5, max: 10 },
  { label: "$10 - $25", min: 10, max: 25 },
  { label: "$25 - $50", min: 25, max: 50 },
  { label: "$50+", min: 50, max: Infinity }
];

// CORRETTO: Bucket per market cap con nomenclatura descrittiva
export const marketCapBuckets: BucketDefinition[] = [
  { label: "Nano (<10M)", min: 0, max: 10e6 },
  { label: "Micro (10M-25M)", min: 10e6, max: 25e6 },
  { label: "Small (25M-100M)", min: 25e6, max: 100e6 },
  { label: "Mid (100M-500M)", min: 100e6, max: 500e6 },
  { label: "Large (500M-1B)", min: 500e6, max: 1e9 },
  { label: "Mega (1B-5B)", min: 1e9, max: 5e9 },
  { label: "Giant (5B+)", min: 5e9, max: Infinity }
];

export const volumeBuckets: BucketDefinition[] = [
  { label: "< 100K", min: 0, max: 100e3 },
  { label: "100K - 500K", min: 100e3, max: 500e3 },
  { label: "500K - 1M", min: 500e3, max: 1e6 },
  { label: "1M - 2.5M", min: 1e6, max: 2.5e6 },
  { label: "2.5M - 5M", min: 2.5e6, max: 5e6 },
  { label: "5M - 10M", min: 5e6, max: 10e6 },
  { label: "10M - 25M", min: 10e6, max: 25e6 },
  { label: "25M - 50M", min: 25e6, max: 50e6 },
  { label: "50M - 100M", min: 50e6, max: 100e6 },
  { label: "100M+", min: 100e6, max: Infinity }
];

// Funzioni di assegnazione bucket
export const getGapBucket = (row: CalculatedMetrics): BucketDefinition | undefined => {
  const gapPercent = calculateGapPercentage(row);
  return gapBuckets.find(b => gapPercent >= b.min && gapPercent < b.max);
};

export const getPriceBucket = (row: CalculatedMetrics): BucketDefinition | undefined => {
  const price = parseFloat(row.Open.toString());
  return priceBuckets.find(b => price >= b.min && price < b.max);
};

export const getMarketCapBucket = (row: CalculatedMetrics): BucketDefinition | undefined => {
  const marketCap = parseFloat(row['Market Cap'].toString());
  return marketCapBuckets.find(b => marketCap >= b.min && marketCap < b.max);
};

export const getVolumeBucket = (row: CalculatedMetrics): BucketDefinition | undefined => {
  const volume = parseFloat(row['Pre Market Volume'].toString());
  return volumeBuckets.find(b => volume >= b.min && volume < b.max);
};

// Funzione per analizzare i dati per bucket
export const analyzeBucketData = (
  data: CalculatedMetrics[], 
  buckets: BucketDefinition[], 
  getBucketFn: (row: CalculatedMetrics) => BucketDefinition | undefined
): BucketAnalysisData[] => {
  return buckets.map(bucket => {
    const gapsInBucket = data.filter(row => {
      const assignedBucket = getBucketFn(row);
      return assignedBucket && assignedBucket.label === bucket.label;
    });
    
    const count = gapsInBucket.length;
    const redCloses = gapsInBucket.filter(row => isRedClose(row)).length;
    const greenCloses = count - redCloses;
    const redClosePercentage = count > 0 ? (redCloses / count) * 100 : 0;
    const percentageOfTotal = data.length > 0 ? (count / data.length) * 100 : 0;
    
    return {
      bucket: bucket.label,
      count,
      percentageOfTotal,
      redCloses,
      greenCloses,
      redClosePercentage
    };
  });
};

// Funzione per filtrare i dati con i filtri globali - RIMOSSO il minimo di 30%
export const applyGlobalFilters = (data: CalculatedMetrics[], filters: GlobalAnalysisFilters): CalculatedMetrics[] => {
  return data.filter(row => {
    // Filtro intervallo date
    if (filters.dateRange[0] && row.Date < filters.dateRange[0]) return false;
    if (filters.dateRange[1] && row.Date > filters.dateRange[1]) return false;

    // Filtro Market Cap
    const marketCap = Number(row['Market Cap']) || 0;
    if (marketCap < filters.marketCapRange[0] || marketCap > filters.marketCapRange[1]) return false;

    // Filtro Gap % - COMPLETAMENTE MODIFICABILE
    const gapPercent = Math.abs(row['Gap %'] * 100);
    if (gapPercent < filters.gapPercentRange[0] || gapPercent > filters.gapPercentRange[1]) return false;

    // Filtro Prezzo
    if (row.Open < filters.priceRange[0] || row.Open > filters.priceRange[1]) return false;

    // Filtro Volume threshold
    if (row['Pre Market Volume'] < filters.volumeThreshold) return false;

    // Filtro tipo chiusura
    if (filters.closeType === 'red' && row['Red Close'] !== 1) return false;
    if (filters.closeType === 'green' && row['Red Close'] !== 0) return false;

    // Filtro PMH Break
    if (filters.pmhBreak === 'break' && row['HIGH > PMH'] !== 'Y') return false;
    if (filters.pmhBreak === 'no-break' && row['HIGH > PMH'] !== 'N') return false;

    return true;
  });
};

// 1. CORRETTO: Analisi per Range di Prezzo Open con etichette numeriche
export const aggregateByPriceRange = (data: CalculatedMetrics[]): PriceBucket[] => {
  // Usa i bucket definiti sopra con etichette numeriche corrette
  return priceBuckets.map(bucket => {
    const bucketData = data.filter(row => 
      row.Open >= bucket.min && row.Open < bucket.max
    );

    if (bucketData.length === 0) {
      return {
        label: bucket.label,
        range: [bucket.min, bucket.max] as [number, number],
        count: 0,
        redCloseRate: 0,
        avgSpike: 0,
        avgVolumeRatio: 0,
        successRate: 0,
        avgVolume: 0,
        pmhBreakRate: 0
      };
    }

    const redCloses = bucketData.filter(row => row['Red Close'] === 1).length;
    const pmhBreaks = bucketData.filter(row => row['HIGH > PMH'] === 'Y').length;
    const spikeValues = bucketData.map(row => (row['Spike %'] || 0) * 100);
    const volumeRatios = bucketData.map(row => row['Vol/PM Vol Ratio'] || 0);
    const volumes = bucketData.map(row => row['Pre Market Volume'] || 0);

    return {
      label: bucket.label,
      range: [bucket.min, bucket.max] as [number, number],
      count: bucketData.length,
      redCloseRate: (redCloses / bucketData.length) * 100,
      avgSpike: safeAverage(spikeValues),
      avgVolumeRatio: safeAverage(volumeRatios),
      successRate: ((bucketData.length - redCloses) / bucketData.length) * 100,
      avgVolume: safeAverage(volumes),
      pmhBreakRate: (pmhBreaks / bucketData.length) * 100
    };
  });
};

// 2. CORRETTO: Analisi per Market Cap con nomenclatura descrittiva
export const aggregateByMarketCap = (data: CalculatedMetrics[]): MarketCapBucket[] => {
  // Usa i bucket definiti sopra con nomenclatura descrittiva corretta
  return marketCapBuckets.map(bucket => {
    const bucketData = data.filter(row => {
      const marketCap = Number(row['Market Cap']) || 0;
      return marketCap >= bucket.min && marketCap < bucket.max;
    });

    if (bucketData.length === 0) {
      return {
        label: bucket.label,
        range: [bucket.min, bucket.max] as [number, number],
        count: 0,
        successRate: 0,
        pmhBreakRate: 0,
        avgSpike: 0,
        avgVolumeRatio: 0,
        redCloseRate: 0
      };
    }

    const redCloses = bucketData.filter(row => row['Red Close'] === 1).length;
    const pmhBreaks = bucketData.filter(row => row['HIGH > PMH'] === 'Y').length;
    const spikeValues = bucketData.map(row => (row['Spike %'] || 0) * 100);
    const volumeRatios = bucketData.map(row => row['Vol/PM Vol Ratio'] || 0);

    return {
      label: bucket.label,
      range: [bucket.min, bucket.max] as [number, number],
      count: bucketData.length,
      successRate: ((bucketData.length - redCloses) / bucketData.length) * 100,
      pmhBreakRate: (pmhBreaks / bucketData.length) * 100,
      avgSpike: safeAverage(spikeValues),
      avgVolumeRatio: safeAverage(volumeRatios),
      redCloseRate: (redCloses / bucketData.length) * 100
    };
  });
};

// 3. Analisi per Gap % - RIMOSSO il minimo di 30%
export const aggregateByGapPercent = (data: CalculatedMetrics[]): GapPercentBucket[] => {
  const buckets = [
    { label: 'Piccolo (0-30%)', range: [0, 30] as [number, number] },
    { label: 'Medio (30-50%)', range: [30, 50] as [number, number] },
    { label: 'Grande (50-100%)', range: [50, 100] as [number, number] },
    { label: 'Enorme (100-200%)', range: [100, 200] as [number, number] },
    { label: 'Estremo (200-500%)', range: [200, 500] as [number, number] },
    { label: 'Mega (>500%)', range: [500, Infinity] as [number, number] }
  ];

  return buckets.map(bucket => {
    const bucketData = data.filter(row => {
      const gapPercent = Math.abs((row['Gap %'] || 0) * 100);
      return gapPercent >= bucket.range[0] && gapPercent < bucket.range[1];
    });

    if (bucketData.length === 0) {
      return {
        ...bucket,
        count: 0,
        redCloseRate: 0,
        avgSpike: 0,
        pmhBreakRate: 0,
        tenuta30min: 0,
        avgVolumeRatio: 0
      };
    }

    const redCloses = bucketData.filter(row => row['Red Close'] === 1).length;
    const pmhBreaks = bucketData.filter(row => row['HIGH > PMH'] === 'Y').length;
    const spikeValues = bucketData.map(row => (row['Spike %'] || 0) * 100);
    const volumeRatios = bucketData.map(row => row['Vol/PM Vol Ratio'] || 0);
    
    // Calcolo tenuta 30min (percentuale di gap che mantengono almeno il 50% del gap iniziale dopo 30 min)
    const tenuta30minCount = bucketData.filter(row => {
      const gapPercent = Math.abs((row['Gap %'] || 0) * 100);
      const thirtyMinHigh = (row['30 Min High %'] || 0) * 100;
      return thirtyMinHigh >= (gapPercent * 0.5);
    }).length;

    return {
      ...bucket,
      count: bucketData.length,
      redCloseRate: (redCloses / bucketData.length) * 100,
      avgSpike: safeAverage(spikeValues),
      pmhBreakRate: (pmhBreaks / bucketData.length) * 100,
      tenuta30min: (tenuta30minCount / bucketData.length) * 100,
      avgVolumeRatio: safeAverage(volumeRatios)
    };
  });
};

// 4. Analisi per Volume PM
export const aggregateByPmVolume = (data: CalculatedMetrics[]): VolumeBucket[] => {
  const buckets = [
    { label: '<1M', range: [0, 1000000] as [number, number] },
    { label: '1-5M', range: [1000000, 5000000] as [number, number] },
    { label: '5-10M', range: [5000000, 10000000] as [number, number] },
    { label: '10-50M', range: [10000000, 50000000] as [number, number] },
    { label: '>50M', range: [50000000, Infinity] as [number, number] }
  ];

  return buckets.map(bucket => {
    const bucketData = data.filter(row => {
      const pmVolume = row['Pre Market Volume'] || 0;
      return pmVolume >= bucket.range[0] && pmVolume < bucket.range[1];
    });

    if (bucketData.length === 0) {
      return {
        ...bucket,
        count: 0,
        successProbability: 0,
        spikeDuration: 0,
        avgVolumeRatio: 0,
        avgSpike: 0
      };
    }

    const redCloses = bucketData.filter(row => row['Red Close'] === 1).length;
    const spikeValues = bucketData.map(row => (row['Spike %'] || 0) * 100);
    const volumeRatios = bucketData.map(row => row['Vol/PM Vol Ratio'] || 0);
    
    // Calcolo durata spike (approssimazione basata su HOD time)
    const spikeDurations = bucketData.map(row => {
      if (row['Hod Time'] instanceof Date) {
        const hodHour = row['Hod Time'].getHours();
        const hodMinute = row['Hod Time'].getMinutes();
        // Assumiamo apertura mercato alle 9:30 (570 minuti dall'inizio giornata)
        const marketOpenMinutes = 9 * 60 + 30;
        const hodMinutes = hodHour * 60 + hodMinute;
        return Math.max(0, hodMinutes - marketOpenMinutes);
      }
      return 60; // Default 60 minuti se non disponibile
    });

    return {
      ...bucket,
      count: bucketData.length,
      successProbability: ((bucketData.length - redCloses) / bucketData.length) * 100,
      spikeDuration: safeAverage(spikeDurations),
      avgVolumeRatio: safeAverage(volumeRatios),
      avgSpike: safeAverage(spikeValues)
    };
  });
};

// 5. Creazione Heatmap Gap % vs Performance - RIMOSSO il minimo di 30%
export const createGapPerformanceHeatmap = (data: CalculatedMetrics[]): HeatmapCell[] => {
  const gapRanges = [
    { label: '0-30%', range: [0, 30] },
    { label: '30-50%', range: [30, 50] },
    { label: '50-100%', range: [50, 100] },
    { label: '100-200%', range: [100, 200] },
    { label: '200-500%', range: [200, 500] },
    { label: '>500%', range: [500, Infinity] }
  ];

  const metrics = [
    'Chiusure Rosse %',
    'Spike Medio %',
    'PMH Break %',
    'Tenuta 30min %',
    'Volume Ratio'
  ];

  const heatmapData: HeatmapCell[] = [];

  gapRanges.forEach(gapRange => {
    const gapData = data.filter(row => {
      const gapPercent = Math.abs((row['Gap %'] || 0) * 100);
      return gapPercent >= gapRange.range[0] && gapPercent < gapRange.range[1];
    });

    if (gapData.length === 0) {
      metrics.forEach(metric => {
        heatmapData.push({
          x: gapRange.label,
          y: metric,
          value: 0,
          count: 0
        });
      });
      return;
    }

    // Calcola metriche per questo range di gap
    const redCloses = gapData.filter(row => row['Red Close'] === 1).length;
    const pmhBreaks = gapData.filter(row => row['HIGH > PMH'] === 'Y').length;
    const spikeValues = gapData.map(row => (row['Spike %'] || 0) * 100);
    const volumeRatios = gapData.map(row => row['Vol/PM Vol Ratio'] || 0);
    
    const tenuta30minCount = gapData.filter(row => {
      const gapPercent = Math.abs((row['Gap %'] || 0) * 100);
      const thirtyMinHigh = (row['30 Min High %'] || 0) * 100;
      return thirtyMinHigh >= (gapPercent * 0.5);
    }).length;

    const metricsValues = {
      'Chiusure Rosse %': (redCloses / gapData.length) * 100,
      'Spike Medio %': safeAverage(spikeValues),
      'PMH Break %': (pmhBreaks / gapData.length) * 100,
      'Tenuta 30min %': (tenuta30minCount / gapData.length) * 100,
      'Volume Ratio': safeAverage(volumeRatios)
    };

    metrics.forEach(metric => {
      heatmapData.push({
        x: gapRange.label,
        y: metric,
        value: metricsValues[metric as keyof typeof metricsValues],
        count: gapData.length
      });
    });
  });

  return heatmapData;
};

// 6. Calcolo Matrice di Correlazione
export const calculateCorrelationMatrix = (data: CalculatedMetrics[]): CorrelationData[] => {
  const variables = [
    { name: 'Gap %', values: data.map(row => (row['Gap %'] || 0) * 100) },
    { name: 'Market Cap', values: data.map(row => Math.log10((Number(row['Market Cap']) || 1))) }, // Log scale
    { name: 'Volume PM', values: data.map(row => Math.log10((row['Pre Market Volume'] || 1))) }, // Log scale
    { name: 'Spike %', values: data.map(row => (row['Spike %'] || 0) * 100) },
    { name: 'Chiusure Rosse', values: data.map(row => row['Red Close'] || 0) },
    { name: 'PMH Break', values: data.map(row => row['HIGH > PMH'] === 'Y' ? 1 : 0) },
    { name: 'Volume Ratio', values: data.map(row => row['Vol/PM Vol Ratio'] || 0) }
  ];

  const correlations: CorrelationData[] = [];

  for (let i = 0; i < variables.length; i++) {
    for (let j = i + 1; j < variables.length; j++) {
      const correlation = calculatePearsonCorrelation(variables[i].values, variables[j].values);
      correlations.push({
        variable1: variables[i].name,
        variable2: variables[j].name,
        correlation,
        significance: Math.abs(correlation) > 0.3 ? 0.95 : 0.5 // Semplificazione
      });
    }
  }

  return correlations;
};

// Funzione helper per calcolare correlazione di Pearson
const calculatePearsonCorrelation = (x: number[], y: number[]): number => {
  const n = Math.min(x.length, y.length);
  if (n === 0) return 0;

  const validPairs = [];
  for (let i = 0; i < n; i++) {
    if (isFinite(x[i]) && isFinite(y[i]) && !isNaN(x[i]) && !isNaN(y[i])) {
      validPairs.push([x[i], y[i]]);
    }
  }

  if (validPairs.length < 2) return 0;

  const sumX = validPairs.reduce((sum, pair) => sum + pair[0], 0);
  const sumY = validPairs.reduce((sum, pair) => sum + pair[1], 0);
  const sumXY = validPairs.reduce((sum, pair) => sum + pair[0] * pair[1], 0);
  const sumX2 = validPairs.reduce((sum, pair) => sum + pair[0] * pair[0], 0);
  const sumY2 = validPairs.reduce((sum, pair) => sum + pair[1] * pair[1], 0);

  const n_valid = validPairs.length;
  const numerator = n_valid * sumXY - sumX * sumY;
  const denominator = Math.sqrt((n_valid * sumX2 - sumX * sumX) * (n_valid * sumY2 - sumY * sumY));

  return denominator === 0 ? 0 : numerator / denominator;
};

// 7. Identificazione Best Setups
export const identifyBestSetups = (data: CalculatedMetrics[]): BestSetup[] => {
  const setups: BestSetup[] = [];

  // Setup Long Bias - Highest success rate
  const priceRanges = aggregateByPriceRange(data);
  const bestPriceRange = priceRanges.reduce((best, current) => 
    current.successRate > best.successRate ? current : best
  );

  // Calcola dati aggiuntivi per il setup Long Bias
  const longBiasData = data.filter(row => 
    row.Open >= bestPriceRange.range[0] && row.Open < bestPriceRange.range[1]
  );
  const longBiasRedCloses = longBiasData.filter(row => row['Red Close'] === 1).length;
  const longBiasPmhBreaks = longBiasData.filter(row => row['HIGH > PMH'] === 'Y').length;

  setups.push({
    title: 'Long Bias',
    description: `Setup ottimale per posizioni long - fascia di prezzo con il più alto tasso di successo`,
    parameters: {
      'Fascia Prezzo': bestPriceRange.label,
      'Tasso Successo': `${bestPriceRange.successRate.toFixed(1)}%`,
      'Tasso Chiusure Rosse': `${bestPriceRange.redCloseRate.toFixed(1)}%`,
      'PMH Break Rate': `${bestPriceRange.pmhBreakRate.toFixed(1)}%`,
      'Spike Medio': `${bestPriceRange.avgSpike.toFixed(1)}%`,
      'Volume Ratio Medio': `${bestPriceRange.avgVolumeRatio.toFixed(1)}x`,
      'Bias Trading': 'LONG'
    },
    successRate: bestPriceRange.successRate,
    avgSpike: bestPriceRange.avgSpike,
    count: bestPriceRange.count,
    variance: 0,
    chartData: [
      { name: 'Successo', value: bestPriceRange.successRate },
      { name: 'Fallimento', value: 100 - bestPriceRange.successRate }
    ]
  });

  // Setup Short Bias - Lowest success rate
  const worstPriceRange = priceRanges.reduce((worst, current) => 
    current.count > 10 && current.successRate < worst.successRate ? current : worst
  );

  // Calcola dati aggiuntivi per il setup Short Bias
  const shortBiasData = data.filter(row => 
    row.Open >= worstPriceRange.range[0] && row.Open < worstPriceRange.range[1]
  );

  setups.push({
    title: 'Short Bias',
    description: `Setup da evitare per posizioni long - fascia di prezzo con il più basso tasso di successo, potenzialmente favorevole per strategie short`,
    parameters: {
      'Fascia Prezzo': worstPriceRange.label,
      'Tasso Successo': `${worstPriceRange.successRate.toFixed(1)}%`,
      'Tasso Chiusure Rosse': `${worstPriceRange.redCloseRate.toFixed(1)}%`,
      'PMH Break Rate': `${worstPriceRange.pmhBreakRate.toFixed(1)}%`,
      'Spike Medio': `${worstPriceRange.avgSpike.toFixed(1)}%`,
      'Volume Ratio Medio': `${worstPriceRange.avgVolumeRatio.toFixed(1)}x`,
      'Bias Trading': 'SHORT'
    },
    successRate: worstPriceRange.successRate,
    avgSpike: worstPriceRange.avgSpike,
    count: worstPriceRange.count,
    variance: 0,
    chartData: [
      { name: 'Successo', value: worstPriceRange.successRate },
      { name: 'Fallimento', value: 100 - worstPriceRange.successRate }
    ]
  });

  // Setup Più Frequente
  const mostFrequent = priceRanges.reduce((most, current) => 
    current.count > most.count ? current : most
  );

  setups.push({
    title: 'Più Frequente',
    description: `Setup più comune nel dataset - rappresenta la configurazione standard di mercato`,
    parameters: {
      'Fascia Prezzo': mostFrequent.label,
      'Occorrenze': mostFrequent.count.toString(),
      'Percentuale Dataset': `${((mostFrequent.count / data.length) * 100).toFixed(1)}%`,
      'Tasso Successo': `${mostFrequent.successRate.toFixed(1)}%`,
      'Tasso Chiusure Rosse': `${mostFrequent.redCloseRate.toFixed(1)}%`,
      'PMH Break Rate': `${mostFrequent.pmhBreakRate.toFixed(1)}%`,
      'Bias Trading': mostFrequent.successRate > 50 ? 'LONG' : 'SHORT'
    },
    successRate: mostFrequent.successRate,
    avgSpike: mostFrequent.avgSpike,
    count: mostFrequent.count,
    variance: 0,
    chartData: [
      { name: 'Questo Setup', value: mostFrequent.count },
      { name: 'Altri Setup', value: data.length - mostFrequent.count }
    ]
  });

  // Hidden Gem - Rare but profitable
  const hiddenGem = priceRanges.find(range => 
    range.count < (data.length * 0.1) && range.successRate > 70 && range.count > 5
  ) || priceRanges[0];

  setups.push({
    title: 'Hidden Gem',
    description: `Setup raro ma altamente profittevole - configurazione di nicchia con performance superiori`,
    parameters: {
      'Fascia Prezzo': hiddenGem.label,
      'Rarità': `${((hiddenGem.count / data.length) * 100).toFixed(1)}%`,
      'Tasso Successo': `${hiddenGem.successRate.toFixed(1)}%`,
      'Tasso Chiusure Rosse': `${hiddenGem.redCloseRate.toFixed(1)}%`,
      'PMH Break Rate': `${hiddenGem.pmhBreakRate.toFixed(1)}%`,
      'Spike Medio': `${hiddenGem.avgSpike.toFixed(1)}%`,
      'Bias Trading': hiddenGem.successRate > 50 ? 'LONG' : 'SHORT'
    },
    successRate: hiddenGem.successRate,
    avgSpike: hiddenGem.avgSpike,
    count: hiddenGem.count,
    variance: 0,
    chartData: [
      { name: 'Successo', value: hiddenGem.successRate },
      { name: 'Spike', value: hiddenGem.avgSpike }
    ]
  });

  // Volume Monster
  const volumeMonster = priceRanges.reduce((monster, current) => 
    current.avgVolumeRatio > monster.avgVolumeRatio ? current : monster
  );

  setups.push({
    title: 'Volume Monster',
    description: `Setup con il più alto volume ratio - configurazione ad alta liquidità e attività di trading`,
    parameters: {
      'Fascia Prezzo': volumeMonster.label,
      'Volume Ratio': `${volumeMonster.avgVolumeRatio.toFixed(1)}x`,
      'Tasso Successo': `${volumeMonster.successRate.toFixed(1)}%`,
      'Tasso Chiusure Rosse': `${volumeMonster.redCloseRate.toFixed(1)}%`,
      'PMH Break Rate': `${volumeMonster.pmhBreakRate.toFixed(1)}%`,
      'Spike Medio': `${volumeMonster.avgSpike.toFixed(1)}%`,
      'Bias Trading': volumeMonster.successRate > 50 ? 'LONG' : 'SHORT'
    },
    successRate: volumeMonster.successRate,
    avgSpike: volumeMonster.avgSpike,
    count: volumeMonster.count,
    variance: 0,
    chartData: [
      { name: 'Volume Ratio', value: volumeMonster.avgVolumeRatio },
      { name: 'Spike', value: volumeMonster.avgSpike }
    ]
  });

  // Consistent - Lowest variance
  const consistentData = priceRanges.map(range => {
    const rangeData = data.filter(row => 
      row.Open >= range.range[0] && row.Open < range.range[1]
    );
    const spikeValues = rangeData.map(row => (row['Spike %'] || 0) * 100);
    const variance = calculateVariance(spikeValues);
    return { ...range, variance };
  });

  const consistent = consistentData.reduce((most, current) => 
    current.count > 10 && current.variance < most.variance ? current : most
  );

  setups.push({
    title: 'Consistent',
    description: `Setup con minor varianza nelle performance - configurazione più prevedibile e stabile`,
    parameters: {
      'Fascia Prezzo': consistent.label,
      'Varianza Spike': consistent.variance.toFixed(2),
      'Tasso Successo': `${consistent.successRate.toFixed(1)}%`,
      'Tasso Chiusure Rosse': `${consistent.redCloseRate.toFixed(1)}%`,
      'PMH Break Rate': `${consistent.pmhBreakRate.toFixed(1)}%`,
      'Spike Medio': `${consistent.avgSpike.toFixed(1)}%`,
      'Bias Trading': consistent.successRate > 50 ? 'LONG' : 'SHORT'
    },
    successRate: consistent.successRate,
    avgSpike: consistent.avgSpike,
    count: consistent.count,
    variance: consistent.variance,
    chartData: [
      { name: 'Consistenza', value: 100 - (consistent.variance / 10) }, // Normalizzato
      { name: 'Performance', value: consistent.avgSpike }
    ]
  });

  return setups;
};

// 8. Creazione Box Plot Data
export const createBoxPlotData = (data: CalculatedMetrics[], metric: 'spike' | 'volumeRatio' | '30minHigh' | 'gap'): BoxPlotData[] => {
  const marketCapBuckets = aggregateByMarketCap(data);
  
  return marketCapBuckets.map(bucket => {
    const bucketData = data.filter(row => {
      const marketCap = Number(row['Market Cap']) || 0;
      return marketCap >= bucket.range[0] && marketCap < bucket.range[1];
    });

    let values: number[] = [];
    switch (metric) {
      case 'spike':
        values = bucketData.map(row => (row['Spike %'] || 0) * 100);
        break;
      case 'volumeRatio':
        values = bucketData.map(row => row['Vol/PM Vol Ratio'] || 0);
        break;
      case '30minHigh':
        values = bucketData.map(row => (row['30 Min High %'] || 0) * 100);
        break;
      case 'gap':
        values = bucketData.map(row => Math.abs((row['Gap %'] || 0) * 100));
        break;
    }

    values = values.filter(v => isFinite(v) && !isNaN(v)).sort((a, b) => a - b);

    if (values.length === 0) {
      return {
        category: bucket.label,
        min: 0,
        q1: 0,
        median: 0,
        q3: 0,
        max: 0,
        mean: 0,
        outliers: []
      };
    }

    const q1Index = Math.floor(values.length * 0.25);
    const medianIndex = Math.floor(values.length * 0.5);
    const q3Index = Math.floor(values.length * 0.75);

    const q1 = values[q1Index];
    const median = values[medianIndex];
    const q3 = values[q3Index];
    const iqr = q3 - q1;
    const lowerFence = q1 - 1.5 * iqr;
    const upperFence = q3 + 1.5 * iqr;

    const outliers = values.filter(v => v < lowerFence || v > upperFence);
    const nonOutliers = values.filter(v => v >= lowerFence && v <= upperFence);

    return {
      category: bucket.label,
      min: nonOutliers.length > 0 ? Math.min(...nonOutliers) : values[0],
      q1,
      median,
      q3,
      max: nonOutliers.length > 0 ? Math.max(...nonOutliers) : values[values.length - 1],
      mean: safeAverage(values),
      outliers
    };
  });
};